package com.milta.web

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.milta.core.localization.LocalLanguageTag
import com.milta.core.localization.LocalStrings
import com.milta.core.network.ApiException
import com.milta.feature.legal.domain.entity.LegalDocument
import com.milta.feature.legal.domain.entity.LegalDocumentType
import com.milta.feature.legal.domain.usecase.GetLegalDocumentUseCase
import org.jetbrains.compose.web.css.maxWidth
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import org.koin.compose.koinInject

/**
 * Simple scrollable page for `/dashboard/legal/terms` and `/dashboard/legal/privacy` (wired from
 * `Main.kt`'s `DashboardRoute`) — reuses the exact same shared [GetLegalDocumentUseCase] as mobile's
 * `PrivacyScreen`/`TermsOfUseScreen`. No elaborate component needed per the ticket: title + content
 * + a back button, styled consistently with [WebHistoriqueScreen]'s cards.
 *
 * Also rendered by the public `/privacy` and `/terms` pages (see [PublicPage]): there [onBack] is
 * `null` (no back link, nothing to go back to), [getLegalDocument] is built on the anonymous HTTP
 * client, [preserveLineBreaks] keeps the single line breaks inside each paragraph and
 * [onDocumentTitle] feeds the browser tab title (loaded title, `null` while loading or on error).
 */
@Composable
fun WebLegalContent(
    type: LegalDocumentType,
    onBack: (() -> Unit)?,
    getLegalDocument: GetLegalDocumentUseCase = koinInject(),
    preserveLineBreaks: Boolean = false,
    onDocumentTitle: (String?) -> Unit = {},
) {
    val languageTag = LocalLanguageTag.current
    val commonStrings = LocalStrings.current.common
    val routeStrings = LocalStrings.current.route
    val profileStrings = LocalStrings.current.profile

    var state by remember(type) { mutableStateOf<WebLegalUiState>(WebLegalUiState.Loading) }
    var reloadKey by remember(type) { mutableStateOf(0) }

    LaunchedEffect(type, reloadKey) {
        state = WebLegalUiState.Loading
        onDocumentTitle(null)
        runCatching { getLegalDocument(type, languageTag) }
            .onSuccess {
                state = WebLegalUiState.Success(it)
                onDocumentTitle(it.title)
            }
            .onFailure { e ->
                val code = if (e is ApiException) "server_error" else "offline"
                state = WebLegalUiState.Error(code)
            }
    }

    Div(attrs = { style { property("display", "flex"); property("flex-direction", "column"); property("gap", "16px") } }) {
        if (onBack != null) {
            Div(
                attrs = {
                    onClick { onBack() }
                    style {
                        property("display", "inline-flex"); property("align-items", "center"); property("gap", "6px")
                        property("cursor", "pointer"); property("font-size", "13px"); property("font-weight", "600")
                        property("color", WebPrimary); property("width", "fit-content")
                    }
                },
            ) { Text("← ${commonStrings.back}") }
        }

        when (val s = state) {
            WebLegalUiState.Loading -> Div(
                attrs = { style { property("padding", "40px"); property("text-align", "center"); property("color", WebTextSecondary); property("font-size", "13px") } },
            ) { Text("…") }

            is WebLegalUiState.Error -> {
                val message = if (s.code == "offline") commonStrings.errorOffline else routeStrings.errorGeneric
                InlineBanner(kind = InlineBannerKind.Error, text = message)
                Button(
                    attrs = {
                        onClick { reloadKey++ }
                        style {
                            property("border", "1px solid $WebBorder"); property("border-radius", "8px")
                            property("background", WebSurface); property("color", WebPrimary)
                            property("font-size", "13px"); property("font-weight", "700")
                            property("padding", "9px 16px"); property("cursor", "pointer"); property("width", "fit-content")
                        }
                    },
                ) { Text(commonStrings.retry) }
            }

            is WebLegalUiState.Success -> WebLegalDocumentCard(
                document = s.document,
                updatedAtPrefix = profileStrings.legalUpdatedAtPrefix,
                preserveLineBreaks = preserveLineBreaks,
            )
        }
    }
}

private sealed interface WebLegalUiState {
    data object Loading : WebLegalUiState
    data class Success(val document: LegalDocument) : WebLegalUiState
    data class Error(val code: String) : WebLegalUiState
}

@Composable
private fun WebLegalDocumentCard(document: LegalDocument, updatedAtPrefix: String, preserveLineBreaks: Boolean) {
    Div(
        attrs = {
            style {
                property("background", WebSurface); property("border", "1px solid $WebBorder")
                property("border-radius", "12px"); padding(28.px); maxWidth(760.px)
            }
        },
    ) {
        Div(attrs = { style { property("font-size", "18px"); property("font-weight", "700"); property("color", WebTextPrimary); property("margin-bottom", "4px") } }) {
            Text(document.title)
        }
        Div(attrs = { style { property("font-size", "12px"); property("color", WebTextSecondary); property("margin-bottom", "20px") } }) {
            Text("$updatedAtPrefix ${formatUpdatedAtWeb(document.updatedAt)}")
        }
        document.content.split("\n\n").forEach { paragraph ->
            if (paragraph.isNotBlank()) {
                Div(
                    attrs = {
                        style {
                            property("font-size", "13.5px"); property("color", WebTextPrimary); property("line-height", "1.6"); property("margin-bottom", "14px")
                            if (preserveLineBreaks) property("white-space", "pre-line")
                        }
                    },
                ) {
                    Text(paragraph)
                }
            }
        }
    }
}

/** "2026-09-10T12:00:00Z" -> "10/09/2026" — same approach as [WebHistoriqueScreen]'s private
 * `formatDateDdMmYyyy`. */
private fun formatUpdatedAtWeb(iso: String): String {
    val datePart = iso.substringBefore('T')
    val parts = datePart.split("-")
    return if (parts.size == 3) "${parts[2]}/${parts[1]}/${parts[0]}" else iso
}
